import type { ReactElement, ReactNode } from 'react';
import { Button as BaseButton } from '@base-ui/react/button';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { Tabs as BaseTabs } from '@base-ui/react/tabs';
import { Toast as BaseToast } from '@base-ui/react/toast';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import { X } from '@phosphor-icons/react';
import clsx from 'clsx';

type ButtonProps = React.ComponentProps<typeof BaseButton> & {
  variant?: 'default' | 'primary' | 'ghost' | 'danger';
  size?: 'default' | 'small';
  iconOnly?: boolean;
};

export function Button({
  variant = 'default',
  size = 'default',
  iconOnly,
  className,
  ...props
}: ButtonProps) {
  return (
    <BaseButton
      className={clsx(
        'dw-button',
        `dw-button--${variant}`,
        size === 'small' && 'dw-button--small',
        iconOnly && 'dw-button--icon',
        className,
      )}
      {...props}
    />
  );
}

export function Tabs({
  value,
  onValueChange,
  tabs,
}: {
  value: string;
  onValueChange: (value: string) => void;
  tabs: Array<{ value: string; label: ReactNode; content: ReactNode }>;
}) {
  return (
    <BaseTabs.Root value={value} onValueChange={onValueChange}>
      <BaseTabs.List className="dw-tabs__list">
        {tabs.map((tab) => (
          <BaseTabs.Tab className="dw-tabs__tab" value={tab.value} key={tab.value}>
            {tab.label}
          </BaseTabs.Tab>
        ))}
        <BaseTabs.Indicator className="dw-tabs__indicator" />
      </BaseTabs.List>
      {tabs.map((tab) => (
        <BaseTabs.Panel className="dw-tabs__panel" value={tab.value} key={tab.value}>
          {tab.content}
        </BaseTabs.Panel>
      ))}
    </BaseTabs.Root>
  );
}

export function Dialog({
  trigger,
  title,
  description,
  children,
  actions,
  aside,
  open,
  onOpenChange,
}: {
  trigger?: ReactElement;
  title: string;
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
  aside?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}>
      {trigger ? <BaseDialog.Trigger render={trigger} /> : null}
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="dw-dialog__backdrop" />
        <BaseDialog.Viewport className="dw-dialog__viewport">
          <BaseDialog.Popup className={clsx('dw-dialog__popup', aside && 'dw-dialog__popup--with-aside')}>
            <div className="dw-dialog__primary">
              <header className="dw-dialog__header">
                <div>
                  <BaseDialog.Title className="dw-dialog__title">{title}</BaseDialog.Title>
                  {description ? (
                    <BaseDialog.Description className="dw-dialog__description">
                      {description}
                    </BaseDialog.Description>
                  ) : null}
                </div>
                <BaseDialog.Close className="dw-dialog__close" aria-label="Close">
                  <X size={15} />
                </BaseDialog.Close>
              </header>
              <div className="dw-dialog__body">{children}</div>
              {actions ? <footer className="dw-dialog__actions">{actions}</footer> : null}
            </div>
            {aside ? <aside className="dw-dialog__aside">{aside}</aside> : null}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

export function Tooltip({ content, children }: { content: ReactNode; children: ReactElement }) {
  return (
    <BaseTooltip.Provider>
      <BaseTooltip.Root>
        <BaseTooltip.Trigger render={children} />
        <BaseTooltip.Portal>
          <BaseTooltip.Positioner sideOffset={6}>
            <BaseTooltip.Popup className="dw-tooltip">{content}</BaseTooltip.Popup>
          </BaseTooltip.Positioner>
        </BaseTooltip.Portal>
      </BaseTooltip.Root>
    </BaseTooltip.Provider>
  );
}

export const toastManager = BaseToast.createToastManager();

function ToastList() {
  const { toasts } = BaseToast.useToastManager();
  return toasts.map((toast) => (
    <BaseToast.Root toast={toast} key={toast.id} className="dw-toast">
      <BaseToast.Content>
        <BaseToast.Title className="dw-toast__title" />
        <BaseToast.Description className="dw-toast__description" />
      </BaseToast.Content>
      <BaseToast.Close className="dw-toast__close" aria-label="Dismiss">
        <X size={14} />
      </BaseToast.Close>
    </BaseToast.Root>
  ));
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <BaseToast.Provider toastManager={toastManager}>
      {children}
      <BaseToast.Portal>
        <BaseToast.Viewport className="dw-toast__viewport">
          <ToastList />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  );
}
